import React, { useRef, useEffect } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button"; // Import Button for the plus/minus buttons
import emailjs from "@emailjs/browser";
import "./Bookingform.css";

function BookingForm({
  showAddressFields = true,
  numOfPeople,
  setNumOfPeople,
  participants = [], // Ensure participants has a default empty array if undefined
  setParticipants,
  city,
  setCity,
  state,
  setState,
  zip,
  setZip,
  email,
  setEmail,
  telefonnumber,
  setTelefonnumber,
  setWishDate,
  remark, 
  setRemark,
  maxGuests = 5,  // Default to 5 if maxGuests is not provided
}) {
  const formRef = useRef();

  // Synchronize participants array with the number of people
  useEffect(() => {
    const newParticipants = [...participants];

    // If numOfPeople is greater than current participants length, add empty participants
    if (numOfPeople > participants.length) {
      for (let i = participants.length; i < numOfPeople; i++) {
        newParticipants.push({ firstName: "", lastName: "" });
      }
    } else if (numOfPeople < participants.length) {
      // If numOfPeople is less, remove extra participants
      newParticipants.length = numOfPeople;
    }

    setParticipants(newParticipants);
  }, [numOfPeople, participants, setParticipants]);

  const schema = Yup.object().shape({
    email: Yup.string()
      .email("Ungültige E-Mail-Adresse")
      .required("Bitte E-Mail Adresse eingeben."),
    telefonnumber: Yup.string().matches(
      /^\d+$/,
      "Telefonnummer muss aus Ziffern bestehen."
    ),
    remark: Yup.string().max(500, "Maximale Länge von 500 Zeichen überschritten"),
    terms: Yup.bool()
      .required()
      .oneOf([true], "AGB müssen akzeptiert werden."),
    dataprotection: Yup.bool()
      .required()
      .oneOf([true], "Datenschutzbestimmungen müssen akzeptiert werden."),
    city: showAddressFields
      ? Yup.string().required("Adresse bitte angeben.")
      : Yup.string(),
    state: showAddressFields
      ? Yup.string().required("Ort bitte angeben.")
      : Yup.string(),
    zip: showAddressFields
      ? Yup.string().required("PLZ bitte angeben.")
      : Yup.string(),
    numOfPeople: Yup.number()
      .min(1, "Mindestens 1 Person")
      .max(5, "Höchstens 5 Personen")
      .required("Bitte die Anzahl der Personen angeben."),
    participants: Yup.array().of(
      Yup.object().shape({
        firstName: Yup.string().required("Vorname bitte angeben."),
        lastName: Yup.string().required("Nachname bitte angeben."),
      })
    ),
  });

  const sendEmail = (values, setSubmitting, resetForm) => {
    const templateParams = {
      participants: values.participants,
      city: values.city,
      state: values.state,
      zip: values.zip,
      email: values.email,
      telefonnumber: values.telefonnumber,
      remark: values.remark,
    };

    emailjs
      .send(
        "service_rjmjpkq",
        "template_j4svqro",
        templateParams,
        "_Vy4_yVElTn8uhx2H"
      )
      .then(
        (result) => {
          console.log(result.text);
          alert("Message sent successfully!");
          setSubmitting(false);
          resetForm(); // Optionally reset the form after successful submission
        },
        (error) => {
          console.log(error.text);
          alert("Failed to send the message. Please try again.");
          setSubmitting(false);
        }
      );
  };

  const handleParticipantsChange = (e, index, field) => {
    const updatedParticipants = [...participants];
    updatedParticipants[index][field] = e.target.value;
    setParticipants(updatedParticipants); // Update participants in parent component
  };

  const incrementPeople = () => {
    setNumOfPeople((prev) => (prev < maxGuests ? prev + 1 : prev));
  };

  const decrementPeople = () => {
    setNumOfPeople((prev) => (prev > 1 ? prev - 1 : prev));
  };

  return (
    <Container>
      <Formik
        validationSchema={schema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          sendEmail(values, setSubmitting, resetForm);
        }}
        initialValues={{
          city: "",
          state: "",
          zip: "",
          email: "",
          telefonnumber: "",
          terms: false,
          dataprotection: false,
          numOfPeople: 1,
          remark: "",
          participants:
            participants || Array(5).fill({ firstName: "", lastName: "" }), // Ensure this is defined
        }}
      >
        {(formik) => (
          <Form
            ref={formRef}
            noValidate
            onSubmit={formik.handleSubmit}
            className="custom-form-background"
          >
            <Row className="mb-5">
              <Form.Group as={Col} md="4" controlId="contactFormNumOfPeople">
                <Form.Label className="mb-0">Anzahl der Personen</Form.Label>
                <div className="d-flex align-items-baseline mt-0">
                  <Button
                    variant="outline-secondary"
                    onClick={decrementPeople}
                    disabled={numOfPeople <= 1}
                    className="mx-2"
                  >
                    -
                  </Button>
                  <span className="mx-2">{numOfPeople}</span>
                  <Button
                    variant="outline-secondary"
                    onClick={incrementPeople}
                    disabled={numOfPeople >= 5}
                    className="mx-2"
                  >
                    +
                  </Button>
                </div>
              </Form.Group>

              {/* Wunschdatum Field */}
              <Form.Group as={Col} md="4" controlId="contactFormWishDate">
                <Form.Label className="mb-0">Wunschdatum</Form.Label>
                <Form.Control
                  type="date"
                  name="wishDate"
                  value={formik.values.wishDate}
                  onChange={(e) => setWishDate(e.target.value)}
                  isInvalid={
                    formik.touched.wishDate && !!formik.errors.wishDate
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.wishDate}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Wenn du weitere Tage hast an denen du die Tour/Reise gerne
                  starten würdest, teile uns diese bitte im Feld Anmerkungen
                  mit.
                </Form.Text>
              </Form.Group>
            </Row>

            {/* Participant Details */}
            <FieldArray name="participants">
              {() => (
                <div>
                  {participants
                    .slice(0, numOfPeople)
                    .map((participant, index) => (
                      <Row className="mb-5" key={index}>
                        <Col md="6">
                          <Form.Label className="mb-0">
                            Vorname (Person {index + 1})
                          </Form.Label>
                          <Form.Control
                            className="mt-1"
                            type="text"
                            name={`participants[${index}].firstName`}
                            value={participant.firstName}
                            onChange={(e) =>
                              handleParticipantsChange(e, index, "firstName")
                            }
                          />
                        </Col>
                        <Col md="6">
                          <Form.Label className="mb-0">
                            Nachname (Person {index + 1})
                          </Form.Label>
                          <Form.Control
                            className="mt-1"
                            type="text"
                            name={`participants[${index}].lastName`}
                            value={participant.lastName}
                            onChange={(e) =>
                              handleParticipantsChange(e, index, "lastName")
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                </div>
              )}
            </FieldArray>

            <Row className="mb-5">
              <Form.Group as={Col} md="6" controlId="contactFormCity">
                <Form.Label className="mb-0">Adresse</Form.Label>
                <Form.Control
                  className="mt-1"
                  type="text"
                  name="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)} // Manually update city
                />
              </Form.Group>
              <Form.Group as={Col} md="3" controlId="contactFormState">
                <Form.Label className="mb-0">Ort</Form.Label>
                <Form.Control
                  className="mt-1"
                  type="text"
                  name="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)} // Manually update state
                />
              </Form.Group>
              <Form.Group as={Col} md="3" controlId="contactFormZip">
                <Form.Label className="mb-0">PLZ</Form.Label>
                <Form.Control
                  className="mt-1"
                  type="text"
                  name="zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)} // Manually update zip
                />
              </Form.Group>
            </Row>

            <Row className="mb-5">
              <Form.Group as={Col} md="6" controlId="contactFormEmail">
                <Form.Label className="mb-0">E-Mail</Form.Label>
                <Form.Control
                  className="mt-1"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)} // Manually update email
                  isValid={formik.touched.email && !formik.errors.email}
                  isInvalid={formik.touched.email && !!formik.errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.email}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="contactFormTelefonnumber">
                <Form.Label className="mb-0">Telefonnummer</Form.Label>
                <Form.Control
                  className="mt-1"
                  type="tel"
                  name="telefonnumber"
                  value={telefonnumber}
                  onChange={(e) => setTelefonnumber(e.target.value)} // Manually update telefonnumber
                  isInvalid={!!formik.errors.telefonnumber}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.telefonnumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="12" controlId="contactFormRemarks">
                <Form.Label className="mb-0">Anmerkungen</Form.Label>
                <Form.Control
                  className="mt-1"
                  as="textarea"
                  rows={3}
                  name="remark"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  isValid={formik.touched.remark && !formik.errors.remark}
                />
              </Form.Group>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default BookingForm;
